import { useSyncExternalStore } from 'react';
import { ApiError, request, setAuthHooks } from '@/api/http';
import { storagePrefix } from '@/config';

export type Session = {
  /** The Authorization header value: "Bearer <jwt>". */
  token: string;
  userName: string;
  /** When the token stops working, in ms since the epoch (from its exp claim). */
  expiresAt: number;
};

export type SignOutReason = 'manual' | 'expired' | 'unauthorized';

const storageKey = `${storagePrefix}session`;

/** A warning shows when less than this is left. */
export const warnBeforeExpiryMs = 60 * 60 * 1000;
/** Signed out this long before the token expires, so no request is ever sent with a token about to fail. */
export const signOutBeforeExpiryMs = 60 * 1000;

/** The exp claim of a JWT in ms, or null when the token cannot be read. The signature is the server's business. */
export function decodeJwtExpiry(jwt: string): number | null {
  const payload = jwt.split('.')[1];
  if (!payload) return null;

  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '='));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function isUsable(session: Session | null, now = Date.now()): session is Session {
  return !!session && session.expiresAt - signOutBeforeExpiryMs > now;
}

function readStored(): Session | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<Session>;
    const session =
      typeof parsed.token === 'string' && typeof parsed.userName === 'string' && typeof parsed.expiresAt === 'number'
        ? { token: parsed.token, userName: parsed.userName, expiresAt: parsed.expiresAt }
        : null;

    return isUsable(session) ? session : null;
  } catch {
    return null;
  }
}

function writeStored(session: Session | null): void {
  try {
    if (session) window.localStorage.setItem(storageKey, JSON.stringify(session));
    else window.localStorage.removeItem(storageKey);
  } catch {
    // Blocked storage: the session lasts for this tab only.
  }
}

let current: Session | null = typeof window === 'undefined' ? null : readStored();
let lastSignOutReason: SignOutReason | null = null;
const listeners = new Set<() => void>();
const timers: ReturnType<typeof setTimeout>[] = [];

function emit(): void {
  for (const listener of listeners) listener();
}

function clearTimers(): void {
  while (timers.length) clearTimeout(timers.pop());
}

// setTimeout fires immediately for delays above about 24.8 days: wait in steps instead.
const maxTimerDelay = 2 ** 31 - 1;

function at(time: number, run: () => void): void {
  const delay = time - Date.now();
  if (delay > maxTimerDelay) timers.push(setTimeout(() => at(time, run), maxTimerDelay));
  else timers.push(setTimeout(run, Math.max(0, delay)));
}

function schedule(): void {
  clearTimers();
  if (!current) return;

  // Re-render the warning when it starts, then sign out a minute before expiry.
  at(current.expiresAt - warnBeforeExpiryMs, emit);
  at(current.expiresAt - signOutBeforeExpiryMs, () => signOut('expired'));
}

function set(session: Session | null): void {
  current = session;
  writeStored(session);
  schedule();
  emit();
}

export function getSession(): Session | null {
  return current;
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSession(): Session | null {
  return useSyncExternalStore(subscribeSession, getSession, getSession);
}

/** Time left before the automatic sign-out, or null when signed out. */
export function msUntilSignOut(now = Date.now()): number | null {
  return current ? current.expiresAt - signOutBeforeExpiryMs - now : null;
}

/** Why the last sign-out happened, read once (the login page explains it). */
export function takeSignOutReason(): SignOutReason | null {
  const reason = lastSignOutReason;
  lastSignOutReason = null;
  return reason;
}

export function signOut(reason: SignOutReason = 'manual'): void {
  if (!current) return;
  lastSignOutReason = reason;
  set(null);
}

/**
 * Signs in. The API answers the token as text ("Bearer <jwt>"), or 401 "Invalid Login". Throws an ApiError with a
 * message for the user.
 */
export async function signIn(userName: string, password: string): Promise<Session> {
  let body: unknown;

  try {
    body = await request('/account/login', { method: 'POST', body: { userName, password }, auth: false });
  } catch (error) {
    if (error instanceof ApiError && error.kind === 'http' && error.status === 401) throw new ApiError('http', 'Wrong user name or password.', 401);
    throw error;
  }

  return adoptToken(body, userName);
}

/** Makes a token the API answered ("Bearer <jwt>" as text) the session's. */
function adoptToken(body: unknown, userName: string): Session {
  const jwt = typeof body === 'string' ? body.trim().replace(/^bearer\s+/i, '') : '';
  const expiresAt = jwt ? decodeJwtExpiry(jwt) : null;

  if (!expiresAt) throw new ApiError('contract', 'The server answered the sign-in without a usable token.');
  if (!isUsable({ token: jwt, userName, expiresAt })) throw new ApiError('contract', 'The server answered with a token that has already expired. Check the server clock.');

  const session: Session = { token: `Bearer ${jwt}`, userName, expiresAt };
  lastSignOutReason = null;
  set(session);

  return session;
}

/**
 * Replaces the signed-in user's token with one for another of their accounts (the answer of POST /Accounts/{id}/switch).
 * Throws when nobody is signed in or the token is unusable; the old session then stays.
 */
export function switchSessionToken(body: unknown): Session {
  if (!current) throw new ApiError('unauthorized', 'Your session has ended. Sign in again.', 401);
  return adoptToken(body, current.userName);
}

setAuthHooks({
  authorization: () => current?.token ?? null,
  onUnauthorized: () => signOut('unauthorized'),
});

if (typeof window !== 'undefined') {
  schedule();

  // A phone that slept past the sign-out time runs late timers late: check again when the app comes back.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && current && !isUsable(current)) signOut('expired');
  });

  // Signing in or out in another tab applies here too.
  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey) return;
    const next = readStored();
    if (next?.token === current?.token) return;
    if (!next && current) lastSignOutReason = 'manual';
    current = next;
    schedule();
    emit();
  });
}
